// SlidingPuzzle.js
import React, { useState, useEffect } from "react";
import { Box, Button, Typography, Card, CardContent, CssBaseline } from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";

const GRID_SIZE = 4;

const generateTiles = () => {
  const tiles = Array.from({ length: GRID_SIZE ** 2 - 1 }, (_, i) => i + 1);
  tiles.push(null);
  return shuffle(tiles);
};

const shuffle = (arr) => arr.sort(() => Math.random() - 0.5);

const SlidingPuzzle = () => {
  const [tiles, setTiles] = useState(generateTiles());
  const [moves, setMoves] = useState(0);
  const [themeMode, setThemeMode] = useState("light");

  const theme = createTheme({
    palette: {
      mode: themeMode,
      primary: { main: themeMode === "light" ? "#1976d2" : "#90caf9" },
      background: {
        default: themeMode === "light" ? "#f4f7fa" : "#0d1117",
        paper: themeMode === "light" ? "#fff" : "#161b22",
      },
    },
    shape: { borderRadius: 12 },
  });

  const moveTile = (index) => {
    const emptyIndex = tiles.indexOf(null);
    const row = Math.floor(index / GRID_SIZE);
    const col = index % GRID_SIZE;
    const emptyRow = Math.floor(emptyIndex / GRID_SIZE);
    const emptyCol = emptyIndex % GRID_SIZE;

    const isAdjacent = Math.abs(row - emptyRow) + Math.abs(col - emptyCol) === 1;
    if (!isAdjacent) return;

    const newTiles = [...tiles];
    [newTiles[index], newTiles[emptyIndex]] = [newTiles[emptyIndex], newTiles[index]];
    setTiles(newTiles);
    setMoves((m) => m + 1);
  };

  const isSolved = tiles.slice(0, -1).every((val, i) => val === i + 1);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          background:
            themeMode === "light"
              ? "linear-gradient(120deg, #f0f4ff, #c3d9ff)"
              : "linear-gradient(120deg, #0f2027, #2c5364)",
        }}
      >
        <Card sx={{ p: 3, borderRadius: 4, boxShadow: 6 }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h5">🧩 Sliding Puzzle</Typography>
              <Button
                onClick={() =>
                  setThemeMode(themeMode === "light" ? "dark" : "light")
                }
              >
                {themeMode === "light" ? "Dark" : "Light"}
              </Button>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: `repeat(${GRID_SIZE}, 60px)`,
                gap: 1,
                mt: 3,
                justifyContent: "center",
              }}
            >
              {tiles.map((num, i) => (
                <Box
                  key={i}
                  onClick={() => moveTile(i)}
                  sx={{
                    width: 60,
                    height: 60,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: 2,
                    backgroundColor: num ? "primary.main" : "transparent",
                    color: num ? "#fff" : "transparent",
                    fontWeight: "bold",
                    fontSize: 18,
                    cursor: num ? "pointer" : "default",
                    transition: "0.2s",
                    "&:hover": {
                      backgroundColor: num ? "primary.dark" : "transparent",
                    },
                  }}
                >
                  {num}
                </Box>
              ))}
            </Box>

            <Typography align="center" mt={2}>
              Moves: {moves}
            </Typography>
            {isSolved && (
              <Typography color="success.main" align="center" mt={1}>
                🎉 Puzzle Solved!
              </Typography>
            )}
            <Box display="flex" justifyContent="center" mt={2}>
              <Button onClick={() => setTiles(generateTiles())}>Shuffle</Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </ThemeProvider>
  );
};

export default SlidingPuzzle;
